import type { Scene } from '../../scene/Scene';
import type { AudioSource } from '../../audio';
import type { Light } from '../../light/Light';
import type { Flame } from '../../object/primitives/Flame';
import { LightFlickerBehaviour } from '../../object/behaviours/LightFlickerBehaviour';
import { CompositeInstance } from '../CompositeInstance';
import { registerTemplate, type Template } from '../Template';
import { buildFlameFromSlot, flameBaseIntensity } from '../flameFactory';
import { buildLightFromSlot } from '../lightFactory';
import { composeAmbientSoundWorldPosition, composeMeshWorldTRS } from '../transform';
import type {
  AmbientSoundSlotDef,
  CompositeDef,
  CompositeState,
  FlameSlotDef,
  LightEmitterState,
  LightSlotDef,
  MeshSlotDef,
  SceneCompositeInstance,
} from '../types';

const TEMPLATE_ID = 'lightEmitter' as const;

const template: Template = {
  id: TEMPLATE_ID,
  stateSchema: {
    emitLight: { kind: 'number', min: 0, max: 1, default: 1 },
  },
  defaultState: { emitLight: 1 } as LightEmitterState,
  buildRuntime(
    def: CompositeDef,
    placement: SceneCompositeInstance,
    mergedState: CompositeState,
    scene: Scene,
  ): CompositeInstance {
    const emit =
      typeof mergedState['emitLight'] === 'number' ? mergedState['emitLight'] : 1;
    const mesh = instantiateMesh(def.slots.mesh, placement, scene);
    const light = instantiateLight(def.slots.light, def.slots.mesh, placement, scene, emit);
    const flicker = new LightFlickerBehaviour(
      light,
      [def.slots.light.color[0], def.slots.light.color[1], def.slots.light.color[2]],
      def.slots.light.intensity,
      def.slots.flicker,
      placement.seed ?? 0,
    );
    const flameSlots = def.slots.flames ?? [];
    const flames = flameSlots.map((slot) =>
      instantiateFlame(slot, def.slots.mesh, placement, scene, emit),
    );
    // Snapshot pre-emit base intensities from the slots, NOT from
    // `flame.intensity` (which `buildFlameFromSlot` already pre-multiplied
    // by `emit`). With `emit = 0` at construction, deriving from the flame
    // would lose the authored value forever.
    const flameBaseIntensities = flameSlots.map(flameBaseIntensity);

    const ambientSound = def.slots.ambientSound
      ? instantiateAmbientSound(def.slots.ambientSound, placement, scene)
      : null;

    if (mesh) mesh.compositeInstanceId = placement.instanceId;
    light.compositeInstanceId = placement.instanceId;

    return new CompositeInstance({
      placement,
      def,
      mesh,
      light,
      flicker,
      flames,
      flameBaseIntensities,
      ambientSound,
      initialState: mergedState,
      scene,
    });
  },
};

function instantiateMesh(slot: MeshSlotDef, placement: SceneCompositeInstance, scene: Scene) {
  if (!slot.modelId || !slot.meshName) return null;
  const obj = scene.objectFactory.instantiateModel(slot.modelId, { meshName: slot.meshName });
  if (!obj) return null;
  const trs = composeMeshWorldTRS(placement, slot);
  obj.trs.position[0] = trs.position[0];
  obj.trs.position[1] = trs.position[1];
  obj.trs.position[2] = trs.position[2];
  obj.trs.setRotationQuat(trs.rotation);
  obj.trs.scale = trs.scale;
  return obj;
}

function instantiateLight(
  slot: LightSlotDef,
  meshSlot: MeshSlotDef,
  placement: SceneCompositeInstance,
  scene: Scene,
  initialEmit: number,
): Light {
  const light = buildLightFromSlot(slot, meshSlot, placement, scene, initialEmit);
  scene.lightManager.addLight(light);
  return light;
}

function instantiateFlame(
  slot: FlameSlotDef,
  meshSlot: MeshSlotDef,
  placement: SceneCompositeInstance,
  scene: Scene,
  initialEmit: number,
): Flame {
  const flame = buildFlameFromSlot(slot, meshSlot, placement, scene, initialEmit);
  scene.flames.push(flame);
  return flame;
}

/**
 * Build a looping AudioSource for the ambient-sound slot, started in `play()`.
 * Returns null when the clip isn't preloaded — preloading is the scene-load
 * path's responsibility (mirrors footstep clip warming). Synchronous so the
 * composite tree builds without going async; that keeps `instantiate.ts`
 * and every caller (`SceneManifestLoader`, `TiledObjectStreamer`, the asset
 * manager's placed-composites registry) on a single call path.
 */
function instantiateAmbientSound(
  slot: AmbientSoundSlotDef,
  placement: SceneCompositeInstance,
  scene: Scene,
): AudioSource | null {
  const source = scene.audio.createSourceFromCached(slot.clipId, {
    bus: 'ambient',
    volume: slot.volume ?? 1,
    loop: true,
    position: composeAmbientSoundWorldPosition(placement, slot),
  });
  source?.play();
  return source;
}

registerTemplate(template);

export { template as lightEmitterTemplate };
